import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LOGO_URL } from "@/lib/api";
import { FermExplainer } from "@/components/FermExplainer";
import { ContactButton } from "@/components/ContactButton";
import { useMember } from "@/context/MemberContext";
import { UserRound } from "lucide-react";

const MemberLink = () => {
  const { member } = useMember();
  return (
    <Link to="/me" data-testid="header-member-link" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E2705A]/60 text-[#FDFBF7] hover:bg-[#E2705A] text-xs font-type uppercase tracking-widest transition-colors">
      <UserRound className="w-3.5 h-3.5" /> {member ? "My ROAN" : "Member sign in"}
    </Link>
  );
};

export const Header = () => (
  <header data-testid="main-header" className="relative bg-[#121E2B] text-[#FDFBF7] grain overflow-hidden">
    <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full bg-[#E2705A]/10 blur-3xl" />
    <div className="absolute left-1/3 -bottom-32 w-[360px] h-[360px] rounded-full bg-[#8FA58A]/10 blur-3xl" />
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-10 sm:pt-12 sm:pb-14">
      <div className="flex justify-end items-center gap-4 mb-6">
        <ContactButton className="!px-3 !py-1.5 !text-xs" />
        <MemberLink />
        <Link
          to="/admin/login"
          data-testid="header-admin-link"
          className="text-xs font-type uppercase tracking-widest text-[#8FA58A] hover:text-[#E2705A] transition-colors"
        >
          Admin
        </Link>
      </div>
      <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-12">
        <motion.img
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          src={LOGO_URL}
          alt="R.O.A.N Initiative logo"
          data-testid="brand-logo"
          className="w-40 sm:w-52 md:w-64 object-contain drop-shadow-[0_18px_40px_rgba(226,112,90,0.35)]"
        />
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            data-testid="hero-title"
            className="font-type font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          >
            The R.O.A.N Initiative
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            data-testid="hero-subtitle"
            className="mt-3 text-base md:text-lg font-semibold tracking-[0.28em] uppercase text-[#8FA58A]"
          >
            Repatriation Of A Nation
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-5 max-w-xl text-sm sm:text-base text-[#FDFBF7]/80 leading-relaxed"
          >
            9 pillars of total system reform to take back the people's power. Correct the wrongs and
            mistakes of the past. Unite the nation and move forward together as one country.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-7 flex items-center gap-3 flex-wrap"
          >
            <a
              href="#join"
              data-testid="hero-join-cta"
              className="inline-flex items-center px-6 py-3 rounded-full bg-[#E2705A] hover:bg-[#D35D46] text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-sm transition-[background-color,transform] hover:-translate-y-0.5 shadow-lg shadow-[#E2705A]/30"
            >
              Get a F.E.R.M. grip on politics
            </a>
            <FermExplainer />
            <Link
              to="/bills"
              data-testid="hero-bill-board-link"
              className="inline-flex items-center px-5 py-3 rounded-full border border-[#FDFBF7]/25 hover:border-[#E2705A] hover:text-[#E2705A] text-sm font-semibold transition-colors"
            >
              National Bill Board
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
    <div className="h-2 bg-[#E2705A]" />
  </header>
);
